import * as React from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useInstance } from "easy-redux-http-call";
import { useAIEventHandler } from "easy-redux-http-call/dist/index";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Divider, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommonButton from "components/common-component/Button";
import CommonTexField from "components/common-component/textField";
import { AI_changePassword } from "APIs/login";
import { AI_getUserData } from "APIs/user";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import PasswordIcon from "@mui/icons-material/Password";
import { ReactComponent as ResetPasswordIcon } from "assets/images/icons/resetPassword.svg";
const useStyle = makeStyles((theme) => ({
  dialog: {
    position: "absolute",
    top: 50,
    width: 500,
    [theme.breakpoints.between("xs", "sm")]: {
      width: "80%",
    },
  },
  dialogTitleContainer: {
    display: "flex",
    gap: "10px",
  },
  dialogTitleTypography: {
    textAlign: "left",
  },
  hrCloseIcon: {
    position: "absolute",
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
}));

const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string().required("Password is required."),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match.")
    .required("Confirm Password is required."),
  old_password: Yup.string().required("Please enter old password"),
});

export default function ChangePassword(props) {
  const classes = useStyle();
  const { openStatus = false, handleCloseAction = () => {} } = props;
  const resetPasswordData = useInstance(AI_changePassword);

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setshowConfirmPassword] = React.useState(false);

  const userData = useInstance(AI_getUserData);

  const handleClickShowPassword = (value) => {
    if (value === "password") {
      setShowPassword(!showPassword);
    } else {
      setshowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChangePassword = () => {
    AI_changePassword.call({
      params: {
        old_password: values.old_password,
        new_password: values.password,
        confirm_new_password: values.confirmPassword,
        employee_id: userData.data.user.employee_id,
      },
    });
  };

  const {
    handleSubmit,
    handleChange,
    errors,
    touched,
    handleBlur,
    values,
  } = useFormik({
    initialValues: {
      old_password: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: ResetPasswordSchema,
    onSubmit: handleChangePassword,
  });

  useAIEventHandler(AI_changePassword, {
    onSuccess: () => {
      handleCloseAction();
    },
  });

  return (
    <Dialog
      open={openStatus}
      onClose={() => handleCloseAction()}
      classes={{ paper: classes.dialog }}
      fullWidth
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <DialogTitle>
          <div className={classes.dialogTitleContainer}>
            <ResetPasswordIcon width={24} height={24} />
            <Typography variant="h4" className={classes.dialogTitleTypography}>
              Reset Password
            </Typography>
          </div>
          <IconButton
            aria-label="close"
            onClick={() => handleCloseAction()}
            className={classes.hrCloseIcon}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <CommonTexField
            label="Old Password"
            name="old_password"
            placeholder="Enter Password"
            type="text"
            value={values?.old_password}
            onChange={(e) => {
              handleChange(e);
            }}
            error={
              touched.old_password && errors?.old_password !== undefined
                ? true
                : false
            }
            helperText={touched.old_password && errors?.old_password}
            onBlur={handleBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon />
                </InputAdornment>
              ),
            }}
          />
          <CommonTexField
            label="Password"
            name="password"
            placeholder="Enter Password"
            type={showPassword ? "text" : "password"}
            value={values?.password}
            onChange={(e) => {
              handleChange(e);
            }}
            error={
              touched.password && errors?.password !== undefined ? true : false
            }
            helperText={touched.password && errors?.password}
            onBlur={handleBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword("password")}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <CommonTexField
            label="Confirm Password"
            name="confirmPassword"
            placeholder="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            value={values.confirmPassword}
            onChange={(e) => {
              handleChange(e);
            }}
            error={
              touched.confirmPassword && errors?.confirmPassword !== undefined
                ? true
                : false
            }
            helperText={touched.confirmPassword && errors?.confirmPassword}
            onBlur={handleBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword("confirm-password")}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {showConfirmPassword ? (
                      <EyeOutlined />
                    ) : (
                      <EyeInvisibleOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => handleCloseAction()}>
            Cancel
          </Button>
          <CommonButton
            className={classes.button}
            type="submit"
            variant="contained"
            color="primary"
            disabled={resetPasswordData.loading}
          >
            Submit
          </CommonButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
